/*
The Radio show form's logic, without React: the form state, what create sends, what edit
sends, and the client-side checks.

THE EDIT RULE. postEditRadioShow validates and writes only the fields present in its
input, so legacy rows whose other fields predate the validation stay editable (242
episodes have duration "0", one hidden show has no Spanish title). Sending the whole form
back would make every such row fail on fields the editor never touched. So edit sends
only what changed against the loaded show:

  - a multilang field (title, subtitle, text) is sent whole if any language changed;
  - `airings` is sent as the full list if anything in it changed (it replaces the set);
  - `tagIds` / `categoryIds` are sent whole if the set changed (order does not count);
  - every other field is sent when its value changed.

The client-side checks follow the same rule: on edit only the changed fields are checked.

Both sides of the comparison go through the same normalisation (showToFormState), so a
null the API returns and the '' an empty input holds compare equal, and an untouched
field is never sent.

CommonJS so radioShowForm.test.js runs under plain node, like cmsTabs.js.
*/
const { madridToUtcIso, utcToMadridParts } = require('./radioTime');
const { SLUG_PATTERN } = require('./radioSlug');

const LANGS = ['es', 'ca', 'en', 'pt'];

const STATUS_OPTIONS = [
  { value: 'visible', label: 'Visible' },
  { value: 'oculta', label: 'Hidden' },
];

const CHANNEL_OPTIONS = [
  { value: '1', label: 'Channel 1' },
  { value: '2', label: 'Channel 2' },
];

// pspanel's select (adminRadioAddEdit.phtml), stored uppercase; empty means none.
const LANG_OPTIONS = [
  { value: '', label: '—' },
  { value: 'ES', label: 'ES' },
  { value: 'EN', label: 'EN' },
  { value: 'CA', label: 'CA' },
];

// Apple Podcasts categories, exactly the lists of the old form (used by the podcast RSS).
const APPLE_CATEGORIES = {
  Music: ['Music Commentary', 'Music History', 'Music Interviews', 'Comedy', 'Society & Culture'],
  'Society & Culture': ['Music', 'Arts', 'Relationships'],
  'Kids & Family': ['Music'],
  'TV & Film': ['Film History', 'Music', 'Comedy'],
  'Health and Fitness': ['Mental Health'],
};

const DURATION_PATTERN = /^\d+:\d{2}$/;

const MULTILANG_FIELDS = ['title', 'subtitle', 'text'];
const BOOLEAN_FIELDS = ['highlight', 'isPlaylist'];
// Held as strings in the form (they back selects and text inputs), sent as Int.
const INT_FIELDS = ['parent', 'channel', 'fileSize'];
const ID_LIST_FIELDS = ['tagIds', 'categoryIds'];

// Every RadioShowInput field, in the order the form shows them.
// The website reads the English title directly and falls back to English for any missing
// language, so es, ca and en are required (ps-back enforces the same; pt is optional).
const REQUIRED_TITLE_LANGS = ['es', 'ca', 'en'];
const LANG_NAMES = {
  es: 'Spanish', ca: 'Catalan', en: 'English', pt: 'Portuguese',
};

const SHOW_FIELDS = [
  'parent', 'slug', ...MULTILANG_FIELDS, 'status', 'channel', 'highlight', 'isPlaylist', 'lang',
  'duration', 'image', 'imageSquare', 'mp3', 'fileSize', 'mixcloud', 'youtube', 'spotify',
  'appleCategory', 'appleSubcategory', ...ID_LIST_FIELDS, 'airings',
];

const str = value => (value === null || value === undefined ? '' : String(value));
// Rich text with only markup left (an emptied editor keeps <p></p>) counts as empty.
const hasText = value => str(value).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';

const toInt = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isInteger(number) ? number : null;
};

const isValidDuration = value => !value || DURATION_PATTERN.test(value);

const emptyMultilang = () => LANGS.reduce((acc, lang) => ({ ...acc, [lang]: '' }), {});

const toFormMultilang = multilang => LANGS.reduce(
  (acc, lang) => ({ ...acc, [lang]: str(multilang && multilang[lang]) }),
  {},
);

// An empty language is sent as null: that is how the migration stores a language that
// was never written, and the public API returns it as is.
const multilangToInput = multilang => LANGS.reduce(
  (acc, lang) => ({ ...acc, [lang]: multilang && multilang[lang] ? multilang[lang] : null }),
  {},
);

const idList = list => (Array.isArray(list) ? list : [])
  .map(Number)
  .filter(Number.isInteger);

/*
Airing rows. `originalIso` / `originalLocal` remember what the API sent, so an untouched
row is sent back with its exact instant (seconds and all) instead of a re-conversion.
*/
let rowCounter = 0;
const nextRowKey = () => {
  rowCounter += 1;
  return `airing-${rowCounter}`;
};

const airingRowFromApi = (airing) => {
  const { date, time } = utcToMadridParts(airing && airing.date);
  return {
    key: nextRowKey(),
    date,
    time,
    channel: str(airing && airing.channel),
    originalIso: airing && airing.date,
    originalLocal: `${date} ${time}`,
  };
};

const newAiringRow = ({ date = '', time = '', channel = '1' } = {}) => ({
  key: nextRowKey(),
  date,
  time,
  channel: str(channel),
  originalIso: null,
  originalLocal: null,
});

const airingKey = row => `${row.date} ${row.time} ${row.channel}`;

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T/;

const airingToInput = (row) => {
  const untouched = row.originalIso && ISO_INSTANT.test(row.originalIso)
    && `${row.date} ${row.time}` === row.originalLocal;
  return {
    date: untouched ? row.originalIso : madridToUtcIso(row.date, row.time),
    channel: toInt(row.channel),
  };
};

const showToFormState = show => ({
  parent: str(show && show.parent !== undefined && show.parent !== null ? show.parent : 0),
  slug: str(show && show.slug),
  title: toFormMultilang(show && show.title),
  subtitle: toFormMultilang(show && show.subtitle),
  text: toFormMultilang(show && show.text),
  status: str(show && show.status),
  channel: str(show && show.channel),
  highlight: Boolean(show && show.highlight),
  isPlaylist: Boolean(show && show.isPlaylist),
  lang: str(show && show.lang),
  duration: str(show && show.duration),
  image: str(show && show.image),
  imageSquare: str(show && show.imageSquare),
  mp3: str(show && show.mp3),
  fileSize: str(show && show.fileSize),
  mixcloud: str(show && show.mixcloud),
  youtube: str(show && show.youtube),
  spotify: str(show && show.spotify),
  appleCategory: str(show && show.appleCategory),
  appleSubcategory: str(show && show.appleSubcategory),
  tagIds: idList(show && show.tagIds),
  categoryIds: idList(show && show.categoryIds),
  airings: ((show && show.airings) || []).map(airingRowFromApi),
});

const emptyFormState = (kind, { parent = '', channel = '1' } = {}) => ({
  ...showToFormState({}),
  parent: kind === 'episodes' ? str(parent) : '0',
  status: 'visible',
  channel: str(channel),
});

const fieldToInput = (field, value) => {
  if (MULTILANG_FIELDS.includes(field)) return multilangToInput(value);
  if (INT_FIELDS.includes(field)) return toInt(value);
  if (BOOLEAN_FIELDS.includes(field)) return Boolean(value);
  if (ID_LIST_FIELDS.includes(field)) return idList(value);
  if (field === 'airings') return (value || []).map(airingToInput);
  return str(value);
};

const sortedIds = list => idList(list).sort((a, b) => a - b).join(',');
const airingSet = rows => (rows || []).map(airingKey).sort().join('|');

const isSameValue = (field, a, b) => {
  if (MULTILANG_FIELDS.includes(field)) return LANGS.every(lang => str(a && a[lang]) === str(b && b[lang]));
  if (BOOLEAN_FIELDS.includes(field)) return Boolean(a) === Boolean(b);
  if (ID_LIST_FIELDS.includes(field)) return sortedIds(a) === sortedIds(b);
  if (field === 'airings') return airingSet(a) === airingSet(b);
  return str(a) === str(b);
};

// The fields the editor changed, in form order.
const dirtyFields = (original, current) => SHOW_FIELDS
  .filter(field => !isSameValue(field, original[field], current[field]));

// The postEditRadioShow input: only the changed fields.
const buildEditInput = (original, current) => dirtyFields(original, current)
  .reduce((input, field) => ({ ...input, [field]: fieldToInput(field, current[field]) }), {});

/*
The postAddRadioShow input: every field. Two exceptions, both "let the backend decide":
an empty slug is left out so the backend generates it, and an empty duration is left out
because '' is not M:SS.
*/
const buildCreateInput = (state, kind) => {
  const input = SHOW_FIELDS.reduce((acc, field) => ({ ...acc, [field]: fieldToInput(field, state[field]) }), {});
  input.parent = kind === 'episodes' ? toInt(state.parent) : 0;
  if (!input.slug) delete input.slug;
  if (!input.duration) delete input.duration;
  return input;
};

// Indexes of airing rows that repeat an earlier row's (date, time, channel).
const duplicateAiringIndexes = (rows) => {
  const seen = new Set();
  return (rows || []).reduce((dupes, row, index) => {
    const key = airingKey(row);
    if (seen.has(key)) dupes.push(index);
    seen.add(key);
    return dupes;
  }, []);
};

/*
Client-side checks. `fields` is what to check: every field on create, the changed ones
on edit. Returns { field: message }; airing rows report as 'airings.<index>' plus one
'airings' summary.
*/
const validateShowForm = (state, { kind, fields = SHOW_FIELDS, isEdit = false } = {}) => {
  const errors = {};
  const checks = new Set(fields);

  if (checks.has('title')) {
    const missing = REQUIRED_TITLE_LANGS.filter(lang => !str(state.title && state.title[lang]).trim());
    if (missing.length) errors.title = `The title is required in ${missing.map(lang => LANG_NAMES[lang]).join(', ')}.`;
  }
  // The website shows a subtitle only when it has an English one.
  if (checks.has('subtitle') && state.subtitle && LANGS.some(lang => hasText(state.subtitle[lang])) && !hasText(state.subtitle.en)) {
    errors.subtitle = 'Add the English subtitle too: the website hides the subtitle without it.';
  }
  if (checks.has('slug')) {
    if (!state.slug) {
      if (isEdit) errors.slug = 'The slug cannot be empty.';
    } else if (!SLUG_PATTERN.test(state.slug)) {
      errors.slug = 'Only lowercase letters, digits, "_" and "-", with no "-" at either end.';
    }
  }
  if (checks.has('parent') && kind === 'episodes' && !(toInt(state.parent) > 0)) {
    errors.parent = 'Choose the program this episode belongs to.';
  }
  if (checks.has('duration') && !isValidDuration(state.duration)) {
    errors.duration = 'Use minutes:seconds, e.g. 58:30.';
  }
  if (checks.has('fileSize') && str(state.fileSize).trim() !== '' && !/^\d+$/.test(str(state.fileSize).trim())) {
    errors.fileSize = 'The file size is a whole number of bytes.';
  }
  if (checks.has('airings')) {
    const rows = state.airings || [];
    rows.forEach((row, index) => {
      if (!madridToUtcIso(row.date, row.time)) {
        errors[`airings.${index}`] = 'Enter a valid date and time.';
      } else if (!['1', '2'].includes(str(row.channel))) {
        errors[`airings.${index}`] = 'Choose channel 1 or 2.';
      }
    });
    duplicateAiringIndexes(rows).forEach((index) => {
      if (!errors[`airings.${index}`]) errors[`airings.${index}`] = 'Same date, time and channel as another row.';
    });
    if (Object.keys(errors).some(key => key.startsWith('airings.'))) {
      errors.airings = 'Fix the highlighted air times.';
    }
  }
  return errors;
};

/*
The backend's { field, message } list, grouped for display: by the field's root
('title.es' and 'title' both go next to the title, 'airings.2.date' next to the air
times), and the ones without a field at the top.
*/
const groupServerErrors = (errors) => {
  const byField = {};
  const general = [];
  (errors || []).forEach((error) => {
    if (!error || !error.message) return;
    const root = error.field ? String(error.field).split(/[.[]/)[0] : '';
    if (!root) {
      general.push(error.message);
      return;
    }
    byField[root] = [...(byField[root] || []), error.message];
  });
  return { byField, general };
};

// Category options, keeping a legacy value that is not in the list so it still shows.
const appleCategoryOptions = (current) => {
  const names = Object.keys(APPLE_CATEGORIES);
  return current && !names.includes(current) ? [...names, current] : names;
};

const appleSubcategoryOptions = (category, current) => {
  const names = APPLE_CATEGORIES[category] || [];
  return current && !names.includes(current) ? [...names, current] : names;
};

module.exports = {
  LANGS,
  REQUIRED_TITLE_LANGS,
  STATUS_OPTIONS,
  CHANNEL_OPTIONS,
  LANG_OPTIONS,
  APPLE_CATEGORIES,
  DURATION_PATTERN,
  SHOW_FIELDS,
  isValidDuration,
  emptyMultilang,
  multilangToInput,
  showToFormState,
  emptyFormState,
  newAiringRow,
  airingKey,
  airingToInput,
  dirtyFields,
  buildEditInput,
  buildCreateInput,
  duplicateAiringIndexes,
  validateShowForm,
  groupServerErrors,
  appleCategoryOptions,
  appleSubcategoryOptions,
  toInt,
};
